/* eslint-disable react/jsx-max-depth */
import { Person } from '@enview/interface/types/Person';
import { faVoteYea } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import _ from 'lodash-es';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import {
  NumberParam,
  NumericArrayParam,
  useQueryParams,
  withDefault,
} from 'use-query-params';
import { JurisdictionAPI, PersonAPI } from '../../api';
import { getSessionOptionsWithIds } from '../../helpers/BillSearchHelper';
import { SortBy, SortOrder } from '../../models/SortOrder';
import { PersonVoteActivity } from '../Shared/ActivityFeed/Activity';
import MultipleSelectDropDown from '../Shared/DropDowns/MultipleSelectDropDown';
import SortDropdown from '../Shared/DropDowns/SortDropdown';
import Timeline from '../Shared/Lists/Timeline';
import NoResults, { NoResultsType, NoResultsPage } from '../Shared/NoResults';
import Paginator from '../Shared/PageElements/Paginator';
import { getJurisdictions } from './PersonHelper';

type PersonVoteHistoryProps = {
  person: Person;
};

const sortOptions = [
  new SortOrder(SortBy.RECENT_ACTIVITY, false),
  new SortOrder(SortBy.RECENT_ACTIVITY, true),
];

const PersonVoteHistory = (props: PersonVoteHistoryProps): JSX.Element => {
  const { person } = props;
  const [sortOrder, setSortOrder] = useState(sortOptions[0]);
  const [jurisdictions, setJurisdictions] = useState<string[]>([]);
  const [loadingPage, setLoadingPage] = useState(true);
  const [selectedSessionIds, setSelectedSessionIds] = useState<number[]>([]);
  const [query, setQuery] = useQueryParams({
    page: withDefault(NumberParam, 1),
    sessionIds: withDefault(NumericArrayParam, []),
  });

  const { data: jurisdictionSessions } =
    JurisdictionAPI.endpoints.getSessions.useQuery(jurisdictions);
  const sessionOptions = getSessionOptionsWithIds(jurisdictionSessions || []);
  const searchParams = useMemo(() => {
    return {
      sessionIds: selectedSessionIds,
      jurisdiction: jurisdictions?.length > 0 ? jurisdictions[0] : undefined,
      includeCurrentSessions: selectedSessionIds?.includes(-1),
      sortOrder: sortOrder.getQueryParam(),
      pagination: {
        page: query.page,
        pageSize: 10,
      },
    };
  }, [sortOrder, selectedSessionIds, jurisdictions, query]);

  const { data: voteData } = PersonAPI.endpoints.getPersonVotes.useQuery({
    personId: person.id,
    searchParams: searchParams,
  });

  useEffect(() => {
    const jurisdictionsForPerson: string[] = getJurisdictions(person);
    setJurisdictions(jurisdictionsForPerson);
  }, [person]);

  useEffect(() => {
    if (voteData) {
      setLoadingPage(false);
    }
  }, [voteData]);

  const updateSessionSelection = (sessionIds: number[]): void => {
    setSelectedSessionIds(sessionIds);
    setQuery({ sessionIds: sessionIds });
  };

  const pages = {
    page: query.page,
    total: voteData ? voteData.count : 0,
    pageSize: 10,
  };

  // Need to directly use this because <SortAndFilterHeader /> only accepts string values
  const filters = (
    <div className="person-vote-filters">
      <div className="row list-header filter-header">
        <div className={`col-xl-5 col-10 d-flex flex-row`}>
          <div className="filter jurisdictions">
            <MultipleSelectDropDown
              currentSelectedValues={selectedSessionIds}
              onChange={(selectedFilterValues) => {
                updateSessionSelection(selectedFilterValues);
              }}
              options={sessionOptions}
              title={`Filter by Session`}
            />
          </div>
        </div>
        <div className={`col-xl-7 col-xs-12`}>
          <SortDropdown
            setSortOrder={(newSortOrder) => {
              setSortOrder(newSortOrder);
            }}
            sortOptions={sortOptions}
            sortOrder={sortOrder}
          />
        </div>
      </div>
    </div>
  );

  const voteEvents = voteData?.data
    ? PersonVoteActivity.transformPersonVotes(voteData?.data)
    : [];
  const votesByDate = _.groupBy(voteEvents, (voteEvent) =>
    voteEvent.date.format('MMMM D, YYYY'),
  );
  const voteMap = new Map<string, ReactNode[]>(
    _.entries(votesByDate).map(([key, value]) => [
      key,
      value.map((activity) => activity.render()),
    ]),
  );

  return (
    <div>
      {loadingPage && (
        <div className="table-spinner-overlay">
          <Spinner animation="border" role="status" />
        </div>
      )}
      {!loadingPage && (
        <>
          {filters}
          {voteMap?.size > 0 && <Timeline eventMap={voteMap} />}
          {(!voteMap || voteMap?.size <= 0) && (
            <NoResults
              icon={<FontAwesomeIcon icon={faVoteYea} size="lg" />}
              page={NoResultsPage.Legislator}
              type={NoResultsType.VoteHistory}
            />
          )}
          <Paginator onChange={(page) => setQuery({ page })} pages={pages} />
        </>
      )}
    </div>
  );
};

export default PersonVoteHistory;
